import React from 'react';
import {
  Settings,
  Mail,
  Phone,
  Building,
  GitMerge,
  AlertTriangle,
  Bell,
  Save,
  Loader2,
  RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useDuplicateConfig, useUpdateDuplicateConfig } from '@/hooks/useDuplicates';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

export const DuplicateConfigPanel: React.FC = () => {
  const { toast } = useToast();
  const { data: config, isLoading, error, refetch } = useDuplicateConfig();
  const updateConfig = useUpdateDuplicateConfig();

  const [localConfig, setLocalConfig] = React.useState({
    enableExactEmail: true,
    enableExactPhone: true,
    enableFuzzyNameCompany: true,
    enableMultipleFields: true,
    fuzzyThreshold: 0.7,
    blockExactDuplicates: false,
    autoCreateSets: true,
    notifyOnDuplicate: true
  });

  // Sync local state with server config
  React.useEffect(() => {
    if (config) {
      setLocalConfig({
        enableExactEmail: config.enableExactEmail,
        enableExactPhone: config.enableExactPhone,
        enableFuzzyNameCompany: config.enableFuzzyNameCompany,
        enableMultipleFields: config.enableMultipleFields,
        fuzzyThreshold: config.fuzzyThreshold,
        blockExactDuplicates: config.blockExactDuplicates,
        autoCreateSets: config.autoCreateSets,
        notifyOnDuplicate: config.notifyOnDuplicate
      });
    }
  }, [config]);

  const handleToggle = (key: keyof typeof localConfig) => {
    setLocalConfig(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleThresholdChange = (value: number) => {
    setLocalConfig(prev => ({
      ...prev,
      fuzzyThreshold: value
    }));
  };

  const handleSave = async () => {
    try {
      await updateConfig.mutateAsync(localConfig);
      toast({
        title: 'Settings saved',
        description: 'Duplicate detection configuration has been updated.',
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to save configuration.',
        variant: 'destructive'
      });
    }
  };

  const handleReset = () => {
    if (config) {
      setLocalConfig({
        enableExactEmail: config.enableExactEmail,
        enableExactPhone: config.enableExactPhone,
        enableFuzzyNameCompany: config.enableFuzzyNameCompany,
        enableMultipleFields: config.enableMultipleFields,
        fuzzyThreshold: config.fuzzyThreshold,
        blockExactDuplicates: config.blockExactDuplicates,
        autoCreateSets: config.autoCreateSets,
        notifyOnDuplicate: config.notifyOnDuplicate
      });
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <p className="text-red-600">Failed to load configuration</p>
          <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4">
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
            <Settings className="h-5 w-5 text-gray-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Duplicate Detection Settings</h2>
            <p className="text-sm text-gray-600">
              Configure how duplicates are detected
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Match Types */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Match Types</h3>
          <div className="space-y-3">
            {/* Exact Email */}
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <div className="flex items-center space-x-3">
                <div className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center",
                  localConfig.enableExactEmail ? "bg-blue-100" : "bg-gray-200"
                )}>
                  <Mail className={cn(
                    "h-4 w-4",
                    localConfig.enableExactEmail ? "text-blue-600" : "text-gray-400"
                  )} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Exact Email Match</p>
                  <p className="text-xs text-gray-500">Detect leads with identical email addresses</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={localConfig.enableExactEmail}
                onChange={() => handleToggle('enableExactEmail')}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
            </label>

            {/* Exact Phone */}
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <div className="flex items-center space-x-3">
                <div className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center",
                  localConfig.enableExactPhone ? "bg-green-100" : "bg-gray-200"
                )}>
                  <Phone className={cn(
                    "h-4 w-4",
                    localConfig.enableExactPhone ? "text-green-600" : "text-gray-400"
                  )} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Exact Phone Match</p>
                  <p className="text-xs text-gray-500">Detect leads with identical phone numbers</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={localConfig.enableExactPhone}
                onChange={() => handleToggle('enableExactPhone')}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
            </label>

            {/* Fuzzy Name/Company */}
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <div className="flex items-center space-x-3">
                <div className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center",
                  localConfig.enableFuzzyNameCompany ? "bg-orange-100" : "bg-gray-200"
                )}>
                  <Building className={cn(
                    "h-4 w-4",
                    localConfig.enableFuzzyNameCompany ? "text-orange-600" : "text-gray-400"
                  )} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Fuzzy Name/Company Match</p>
                  <p className="text-xs text-gray-500">Detect leads with similar names and companies</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={localConfig.enableFuzzyNameCompany}
                onChange={() => handleToggle('enableFuzzyNameCompany')}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
            </label>

            {/* Multiple Fields */}
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <div className="flex items-center space-x-3">
                <div className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center",
                  localConfig.enableMultipleFields ? "bg-purple-100" : "bg-gray-200"
                )}>
                  <GitMerge className={cn(
                    "h-4 w-4",
                    localConfig.enableMultipleFields ? "text-purple-600" : "text-gray-400"
                  )} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Multiple Field Match</p>
                  <p className="text-xs text-gray-500">Detect leads with multiple matching fields</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={localConfig.enableMultipleFields}
                onChange={() => handleToggle('enableMultipleFields')}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
            </label>
          </div>
        </div>

        {/* Fuzzy Threshold */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Fuzzy Match Threshold</h3>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Similarity threshold</span>
              <span className="text-sm font-medium text-gray-900">
                {Math.round(localConfig.fuzzyThreshold * 100)}%
              </span>
            </div>
            <input
              type="range"
              min="0.5"
              max="1"
              step="0.05"
              value={localConfig.fuzzyThreshold}
              onChange={(e) => handleThresholdChange(parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>50% (Loose)</span>
              <span>100% (Exact)</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Lower threshold = more potential matches, higher = fewer but more accurate
            </p>
          </div>
        </div>

        {/* Behavior Settings */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Behavior</h3>
          <div className="space-y-3">
            {/* Block Exact Duplicates */}
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <div className="flex items-center space-x-3">
                <AlertTriangle className={cn(
                  "h-4 w-4",
                  localConfig.blockExactDuplicates ? "text-red-600" : "text-gray-400"
                )} />
                <div>
                  <p className="text-sm font-medium text-gray-900">Block Exact Duplicates</p>
                  <p className="text-xs text-gray-500">Prevent creation of leads with exact email/phone match</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={localConfig.blockExactDuplicates}
                onChange={() => handleToggle('blockExactDuplicates')}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
            </label>

            {/* Auto Create Sets */}
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <div className="flex items-center space-x-3">
                <GitMerge className={cn(
                  "h-4 w-4",
                  localConfig.autoCreateSets ? "text-blue-600" : "text-gray-400"
                )} />
                <div>
                  <p className="text-sm font-medium text-gray-900">Auto-create Duplicate Sets</p>
                  <p className="text-xs text-gray-500">Automatically group detected duplicates into sets</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={localConfig.autoCreateSets}
                onChange={() => handleToggle('autoCreateSets')}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
            </label>

            {/* Notify on Duplicate */}
            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <div className="flex items-center space-x-3">
                <Bell className={cn(
                  "h-4 w-4",
                  localConfig.notifyOnDuplicate ? "text-blue-600" : "text-gray-400"
                )} />
                <div>
                  <p className="text-sm font-medium text-gray-900">Notify on Duplicate</p>
                  <p className="text-xs text-gray-500">Send notification when duplicates are detected</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={localConfig.notifyOnDuplicate}
                onChange={() => handleToggle('notifyOnDuplicate')}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
            </label>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex space-x-3 pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handleReset}
            className="flex-1"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateConfig.isPending}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            {updateConfig.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
};
